import { CommonModule, formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { EMPTY, forkJoin, of, Subject } from 'rxjs';
import { catchError, expand, finalize, map, reduce, takeUntil } from 'rxjs/operators';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Driver } from '../../driver/driver.model';
import { DriverService } from '../../driver/driver.service';
import { Vehicle } from '../../vehicle/vehicle.model';
import { VehicleService } from '../../vehicle/vehicle.service';
import { FleetTrailer } from '../../trailer/models/trailer.model';
import { FleetTrailerService } from '../../trailer/trailer.service';
import { LogisticsProjectsService } from '../../../logistics-projects/logistics-projects.service';
import { LogisticsProjectSummary } from '../../../logistics-projects/models/logistics-project.model';
import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { HrService } from '../../../hr/hr.service';
import { HrEmployee, HrWorkforceReadinessRuleResult, HrWorkforceReadinessSnapshot } from '../../../hr/hr.models';
import { Mobilization, MobilizationProjectSource } from '../models/mobilization.model';
import { MobilizationService } from '../mobilization.service';

@Component({
    selector: 'app-mobilization-create',
    standalone: true,
    templateUrl: './mobilization-create.component.html',
    styleUrls: ['./mobilization-create.component.css'],
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        DatePickerModule,
        IftaLabelModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputNumberModule,
        InputTextModule,
        SelectModule,
        TagModule,
        TextareaModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent
    ]
})
export class MobilizationCreateComponent implements OnInit, OnDestroy {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    form!: FormGroup;
    drivers: Driver[] = [];
    vehicles: Vehicle[] = [];
    trailers: FleetTrailer[] = [];
    selectedProject: LogisticsProjectSummary | null = null;
    supervisors: HrEmployee[] = [];
    isLoading = false;
    isSaving = false;
    mobilizationId?: number;
    existing?: Mobilization;
    driverReadiness?: HrWorkforceReadinessSnapshot;
    driverReadinessLoading = false;
    driverReadinessError?: string;

    readonly projectSourceOptions = [
        { label: 'Logistics Project', value: 'LOGISTICS_PROJECT' },
        { label: 'Manual Project/Site', value: 'MANUAL' }
    ];

    readonly assignmentRoleOptions = [
        { label: 'Driver', value: 'Driver' },
        { label: 'Route Support', value: 'Route Support' },
        { label: 'Merchandiser', value: 'Merchandiser' },
        { label: 'Supervisor', value: 'Supervisor' },
        { label: 'Technician', value: 'Technician' }
    ];

    private readonly destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private mobilizationService: MobilizationService,
        private hrService: HrService,
        private driverService: DriverService,
        private vehicleService: VehicleService,
        private trailerService: FleetTrailerService,
        private logisticsProjectsService: LogisticsProjectsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.mobilizationId = Number(this.route.snapshot.paramMap.get('id')) || undefined;
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Mobilization', routerLink: '/fleet/mobilization' },
            { label: this.mobilizationId ? 'Edit' : 'New' }
        ];
        this.buildForm();
        this.watchProjectSource();
        this.watchDriverReadiness();
        this.loadReferenceData();
        if (this.mobilizationId) {
            this.loadMobilization(this.mobilizationId);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    saveDraft(): void {
        this.save(false);
    }

    submit(): void {
        this.save(true);
    }

    summaryDriver(): string {
        const driver = this.form?.value.driver as Driver | null;
        return driver?.name || driver?.legalName || this.existing?.driverName || 'Driver not selected';
    }

    summaryVehicle(): string {
        const vehicle = this.form?.value.vehicle as Vehicle | null;
        return vehicle?.plateNumber || vehicle?.registrationNumber || this.existing?.vehiclePlateNumber || 'Vehicle not selected';
    }

    summaryTrailer(): string {
        const trailer = this.form?.value.trailer as FleetTrailer | null;
        return trailer?.assetNumber || trailer?.plateNumber || this.existing?.trailerAssetNumber || 'No trailer selected';
    }

    summaryProject(): string {
        const source = this.form?.value.projectSource as MobilizationProjectSource;
        if (source === 'LOGISTICS_PROJECT') {
            return this.selectedLogisticsProjectLabel(this.form?.value.logisticsProjectId);
        }
        return [this.form?.value.manualProjectCode, this.form?.value.manualProjectName].filter(Boolean).join(' - ') || 'Project/site not assigned';
    }

    summaryDates(): string {
        const start = this.form?.value.startDate;
        const end = this.form?.value.endDate;
        if (!start || !end) {
            return 'Dates not selected';
        }
        return `${formatDate(start, 'MMM d, y', 'en-US')} - ${formatDate(end, 'MMM d, y', 'en-US')}`;
    }

    readinessStatusLabel(): string {
        if (this.driverReadinessLoading) {
            return 'Evaluating';
        }
        return this.formatReadiness(this.driverReadiness?.readinessStatus) || 'Not evaluated';
    }

    readinessSummary(): string {
        if (this.driverReadinessLoading) {
            return 'Checking HR employee link, documents, qualifications, and active employment.';
        }
        if (this.driverReadiness) {
            return `${this.driverReadiness.blockerCount ?? 0} blockers, ${this.driverReadiness.warningCount ?? 0} warnings. Expires ${this.formatDateTime(this.driverReadiness.expiresAt)}.`;
        }
        return this.driverReadinessError || 'Select a driver to preview HR workforce readiness.';
    }

    readinessPrimaryIssue(): string {
        const issue = this.driverReadinessRuleResults()
            .find(result => result.blocker || result.warning || result.resultStatus === 'FAIL' || result.resultStatus === 'WARN');
        return issue?.message || 'No blocking HR readiness issue is currently visible.';
    }

    readinessSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'READY') return 'success';
        if (status === 'WARNING') return 'warn';
        if (status === 'BLOCKED') return 'danger';
        if (status === 'OVERRIDDEN') return 'info';
        return 'secondary';
    }

    isLogisticsProjectMode(): boolean {
        return this.form?.value.projectSource === 'LOGISTICS_PROJECT';
    }

    private buildForm(): void {
        const today = this.startOfToday();
        const nextMonth = this.addMonths(today, 1);
        this.form = this.fb.group({
            driver: [null, Validators.required],
            vehicle: [null, Validators.required],
            trailer: [null],
            logisticsProjectId: [Number(this.route.snapshot.queryParamMap.get('projectId') || '') || null],
            projectSource: ['LOGISTICS_PROJECT', Validators.required],
            manualProjectCode: [''],
            manualProjectName: [''],
            manualSiteName: [''],
            manualSiteAddress: [''],
            roleName: ['Driver'],
            supervisor: [null],
            startDate: [today, Validators.required],
            endDate: [nextMonth, Validators.required],
            expectedHoursPerWeek: [48],
            salaryManualAdjustment: [0],
            shiftNotes: [''],
            notes: ['']
        });
        this.applyProjectSourceMode(this.normalizeProjectSource(this.form.value.projectSource));
    }

    private watchProjectSource(): void {
        this.form.get('projectSource')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((source: MobilizationProjectSource) => this.applyProjectSourceMode(this.normalizeProjectSource(source)));
    }

    private watchDriverReadiness(): void {
        this.form.get('driver')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((driver: Driver | null) => this.evaluateDriverReadiness(driver));
    }

    private evaluateDriverReadiness(driver: Driver | null): void {
        this.driverReadiness = undefined;
        this.driverReadinessError = undefined;
        if (!driver?.id) {
            return;
        }

        this.driverReadinessLoading = true;
        this.hrService.evaluateReadiness({
            driverId: driver.id,
            assignmentType: 'FLEET_MOBILIZATION',
            requiredWorkerCategory: 'DRIVER',
            assignmentResourceId: this.mobilizationId,
            sourceModule: 'FLEET_MOBILIZATION',
            sourceResourceId: this.mobilizationId,
            forceRefresh: false
        }).pipe(finalize(() => this.driverReadinessLoading = false))
            .subscribe({
                next: response => {
                    this.driverReadiness = response.data?.snapshots?.[0];
                    if (!this.driverReadiness) {
                        this.driverReadinessError = 'No linked HR employee readiness snapshot was returned for this driver.';
                    }
                },
                error: () => {
                    this.driverReadinessError = 'HR readiness preview is unavailable for this user or tenant.';
                }
            });
    }

    private loadReferenceData(): void {
        const company = this.sessionStorageService.getCompany();
        const companyId = company?.id ?? 0;
        const transportationAgencyId = company?.transportationAgencyId;
        this.isLoading = true;

        forkJoin({
            ownedDrivers: transportationAgencyId ? this.driverService.findDriversByTransportationAgencyId(0, 250, transportationAgencyId).pipe(catchError(() => of({ items: [] } as any))) : of({ items: [] } as any),
            freelanceDrivers: companyId ? this.driverService.getFreelanceDriversByCompanyId(0, 250, companyId).pipe(catchError(() => of({ items: [] } as any))) : of({ items: [] } as any),
            ownedVehicles: transportationAgencyId ? this.vehicleService.findVehiclesByTransportationAgencyId(0, 250, transportationAgencyId).pipe(catchError(() => of({ items: [] } as any))) : of({ items: [] } as any),
            freelanceVehicles: companyId ? this.vehicleService.getFreelanceVehiclesByCompanyId(0, 250, companyId).pipe(catchError(() => of({ items: [] } as any))) : of({ items: [] } as any),
            trailers: companyId ? this.trailerService.searchTrailers({ owningCompanyId: companyId, page: 0, pageSize: 250, active: true }).pipe(catchError(() => of({ items: [] } as any))) : of({ items: [] } as any),
            supervisors: companyId ? this.loadAllSupervisors(companyId).pipe(catchError(() => of([]))) : of([])
        }).pipe(finalize(() => this.isLoading = false))
            .subscribe(result => {
                this.drivers = this.uniqueById([...(result.ownedDrivers.items ?? []), ...(result.freelanceDrivers.items ?? [])], 'id');
                this.vehicles = this.uniqueById([...(result.ownedVehicles.items ?? []), ...(result.freelanceVehicles.items ?? [])], 'vehicleId');
                this.trailers = result.trailers.items ?? [];
                this.supervisors = result.supervisors ?? [];
                if (this.existing) {
                    this.patchForm(this.existing);
                }
            });
    }

    private loadMobilization(id: number): void {
        this.isLoading = true;
        this.mobilizationService.getMobilization(id)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: response => {
                    this.existing = response.data;
                    this.patchForm(response.data);
                },
                error: () => this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load mobilization.' })
            });
    }

    private patchForm(mobilization: Mobilization): void {
        if (!this.form) {
            return;
        }
        const driver = this.drivers.find(item => item.id === mobilization.driverId) ?? null;
        const vehicle = this.vehicles.find(item => item.vehicleId === mobilization.vehicleId) ?? null;
        const trailer = this.trailers.find(item => item.trailerId === mobilization.trailerId)
            ?? (mobilization.trailerId ? {
                trailerId: mobilization.trailerId,
                assetNumber: mobilization.trailerAssetNumber,
                trailerTypeId: mobilization.trailerTypeId,
                trailerTypeCode: mobilization.trailerTypeCode,
                trailerTypeName: mobilization.trailerTypeName,
                plateNumber: mobilization.trailerPlateNumber,
                registrationNumber: mobilization.trailerRegistrationNumber
            } as FleetTrailer : null);
        const supervisor = this.findSupervisorByName(mobilization.supervisorName);

        this.form.patchValue({
            driver,
            vehicle,
            trailer,
            logisticsProjectId: mobilization.logisticsProjectId ?? null,
            projectSource: this.normalizeProjectSource(mobilization.projectSource),
            manualProjectCode: mobilization.manualProjectCode ?? '',
            manualProjectName: mobilization.manualProjectName ?? '',
            manualSiteName: mobilization.manualSiteName ?? '',
            manualSiteAddress: mobilization.manualSiteAddress ?? '',
            roleName: mobilization.roleName ?? 'Driver',
            supervisor,
            startDate: mobilization.startDate ? new Date(mobilization.startDate) : this.startOfToday(),
            endDate: mobilization.endDate ? new Date(mobilization.endDate) : this.addMonths(this.startOfToday(), 1),
            expectedHoursPerWeek: mobilization.expectedHoursPerWeek ?? 48,
            salaryManualAdjustment: mobilization.salaryManualAdjustment ?? 0,
            shiftNotes: mobilization.shiftNotes ?? '',
            notes: mobilization.notes ?? ''
        });
    }

    private save(submitAfterSave: boolean): void {
        if (this.form.invalid || !this.validateDateRange()) {
            this.form.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Missing details', detail: 'Complete the required assignment and schedule fields.' });
            return;
        }

        const vehicle = this.form.value.vehicle as Vehicle;
        if (!vehicle?.type?.id) {
            this.messageService.add({ severity: 'warn', summary: 'Truck type missing', detail: 'Selected vehicle must have a truck type before mobilization salary can be calculated.' });
            return;
        }

        this.isSaving = true;
        const payload = this.toPayload();
        const request = this.mobilizationId
            ? this.mobilizationService.updateMobilization(this.mobilizationId, payload)
            : this.mobilizationService.createMobilization(payload);

        request.subscribe({
            next: response => {
                const saved = response.data;
                if (submitAfterSave && saved.mobilizationId) {
                    this.mobilizationService.submit(saved.mobilizationId, this.actorAction('Submitted from assignment form.'))
                        .pipe(finalize(() => this.isSaving = false))
                        .subscribe({
                            next: submitted => this.linkProjectRequirementFulfillment(submitted.data)
                                .add(() => this.router.navigate(['/fleet/mobilization', submitted.data.mobilizationId])),
                            error: () => this.messageService.add({ severity: 'error', summary: 'Submit failed', detail: 'Draft was saved but could not be submitted.' })
                        });
                } else {
                    this.isSaving = false;
                    this.linkProjectRequirementFulfillment(saved)
                        .add(() => this.router.navigate(['/fleet/mobilization', saved.mobilizationId]));
                }
            },
            error: error => {
                this.isSaving = false;
                this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || 'Could not save mobilization.' });
            }
        });
    }

    private toPayload(): Mobilization {
        const value = this.form.value;
        const driver = value.driver as Driver;
        const vehicle = value.vehicle as Vehicle;
        const trailer = value.trailer as FleetTrailer | null;
        const supervisor = value.supervisor as HrEmployee | null;
        const source = this.normalizeProjectSource(value.projectSource);
        const logisticsProjectId = source === 'LOGISTICS_PROJECT' ? value.logisticsProjectId || undefined : undefined;

        return {
            ...this.existing,
            mobilizationId: this.mobilizationId,
            owningCompanyId: this.companyId(),
            transportationAgencyId: this.sessionStorageService.getCompany()?.transportationAgencyId ?? undefined,
            driverId: driver.id,
            driverName: driver.name || driver.legalName,
            driverPhoneNumber: driver.phoneNumber,
            driverLicenseNumber: driver.licenseNumber,
            vehicleId: vehicle.vehicleId,
            vehiclePlateNumber: vehicle.plateNumber,
            vehicleRegistrationNumber: vehicle.registrationNumber,
            vehicleTypeName: vehicle.type?.name,
            vehicleTypeId: vehicle.type?.id,
            trailerId: trailer?.trailerId,
            trailerAssetNumber: trailer?.assetNumber,
            trailerTypeId: trailer?.trailerTypeId,
            trailerTypeCode: trailer?.trailerTypeCode,
            trailerTypeName: trailer?.trailerTypeName,
            trailerPlateNumber: trailer?.plateNumber,
            trailerRegistrationNumber: trailer?.registrationNumber,
            projectSource: source,
            logisticsProjectId,
            manualProjectCode: source === 'MANUAL' ? value.manualProjectCode || undefined : undefined,
            manualProjectName: source === 'MANUAL' ? value.manualProjectName || undefined : undefined,
            manualSiteName: source === 'MANUAL' ? value.manualSiteName || undefined : undefined,
            manualSiteAddress: source === 'MANUAL' ? value.manualSiteAddress || undefined : undefined,
            roleName: value.roleName,
            supervisorEmployeeId: supervisor?.employeeId,
            supervisorName: this.employeeLabel(supervisor) || undefined,
            startDate: value.startDate,
            endDate: value.endDate,
            expectedHoursPerWeek: value.expectedHoursPerWeek,
            salaryManualAdjustment: value.salaryManualAdjustment,
            shiftNotes: value.shiftNotes,
            notes: value.notes,
            status: this.existing?.status ?? 'DRAFT'
        };
    }

    private validateDateRange(): boolean {
        const start = this.form.value.startDate ? new Date(this.form.value.startDate) : null;
        const end = this.form.value.endDate ? new Date(this.form.value.endDate) : null;
        return !!start && !!end && end >= start;
    }

    private actorAction(comment: string) {
        const user = this.sessionStorageService.getCustomer();
        return {
            actorCustomerId: user?.id,
            actorName: user?.name || user?.emailAddress || 'Current user',
            comment
        };
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }

    private selectedLogisticsProjectLabel(projectId?: number | null): string {
        if (!projectId) {
            return 'Project/site not assigned';
        }
        const project = this.selectedProject?.projectId === projectId ? this.selectedProject : null;
        if (!project) {
            return `Logistics Project #${projectId}`;
        }
        return [project.projectNumber, project.projectName].filter(Boolean).join(' - ') || `Logistics Project #${projectId}`;
    }

    private normalizeProjectSource(value?: string | null): MobilizationProjectSource {
        return value === 'MANUAL' ? 'MANUAL' : 'LOGISTICS_PROJECT';
    }

    private applyProjectSourceMode(source: MobilizationProjectSource): void {
        const logisticsProjectId = this.form.get('logisticsProjectId');
        const manualProjectCode = this.form.get('manualProjectCode');
        const manualProjectName = this.form.get('manualProjectName');
        const manualSiteName = this.form.get('manualSiteName');
        const manualSiteAddress = this.form.get('manualSiteAddress');

        if (source === 'LOGISTICS_PROJECT') {
            logisticsProjectId?.clearValidators();
            manualProjectName?.clearValidators();
            manualSiteName?.clearValidators();
            this.form.patchValue({
                manualProjectCode: '',
                manualProjectName: '',
                manualSiteName: '',
                manualSiteAddress: ''
            }, { emitEvent: false });
        } else {
            logisticsProjectId?.clearValidators();
            manualProjectName?.clearValidators();
            manualSiteName?.clearValidators();
            this.form.patchValue({ logisticsProjectId: null }, { emitEvent: false });
        }

        logisticsProjectId?.updateValueAndValidity({ emitEvent: false });
        manualProjectCode?.updateValueAndValidity({ emitEvent: false });
        manualProjectName?.updateValueAndValidity({ emitEvent: false });
        manualSiteName?.updateValueAndValidity({ emitEvent: false });
        manualSiteAddress?.updateValueAndValidity({ emitEvent: false });
    }

    private startOfToday(): Date {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    private addMonths(date: Date, months: number): Date {
        const result = new Date(date);
        result.setMonth(result.getMonth() + months);
        return result;
    }

    private loadAllSupervisors(companyId: number) {
        return this.hrService.employees({ owningCompanyId: companyId, page: 0, pageSize: 200 }).pipe(
            expand(page => page.hasMore ? this.hrService.employees({
                owningCompanyId: companyId,
                page: (page.offset ?? 0) + 1,
                pageSize: 200
            }) : EMPTY),
            map(page => page.items ?? []),
            reduce((all, items) => [...all, ...items], [] as HrEmployee[])
        );
    }

    private uniqueById<T>(items: T[], key: keyof T): T[] {
        const seen = new Set<unknown>();
        return items.filter(item => {
            const value = item[key];
            if (!value || seen.has(value)) {
                return false;
            }
            seen.add(value);
            return true;
        });
    }

    private driverReadinessRuleResults(): HrWorkforceReadinessRuleResult[] {
        if (!this.driverReadiness?.ruleResultsJson) {
            return [];
        }
        try {
            const parsed = JSON.parse(this.driverReadiness.ruleResultsJson);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private formatReadiness(value?: string): string {
        return (value || '').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
    }

    private findSupervisorByName(name?: string): HrEmployee | null {
        const existingSupervisorId = this.existing?.supervisorEmployeeId;
        if (existingSupervisorId) {
            const byId = this.supervisors.find(employee => employee.employeeId === existingSupervisorId);
            if (byId) {
                return byId;
            }
        }
        if (!name) {
            return null;
        }
        return this.supervisors.find(employee => this.employeeLabel(employee) === name || employee.displayName === name) ?? null;
    }

    employeeLabel(employee?: HrEmployee | null): string {
        return employee?.displayName || [employee?.firstName, employee?.lastName].filter(Boolean).join(' ') || '';
    }

    private formatDateTime(value?: string): string {
        if (!value) {
            return '-';
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '-' : formatDate(date, 'MMM d, y h:mm a', 'en-US');
    }

    private linkProjectRequirementFulfillment(mobilization: Mobilization) {
        const query = this.route.snapshot.queryParamMap;
        const projectId = Number(query.get('projectId') || query.get('logisticsProjectId') || mobilization.logisticsProjectId || 0);
        const requirementId = Number(query.get('requirementId') || 0);
        const owningCompanyId = this.companyId();
        if (!projectId || !requirementId || !owningCompanyId || !mobilization.mobilizationId) {
            return { add: (callback: () => void) => callback() };
        }
        const quantity = Number(query.get('quantity') || 1);
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: query.get('targetResourceType') || 'FLEET_MOBILIZATION',
            targetResourceId: mobilization.mobilizationId,
            sourceModule: 'FLEET',
            sourceReference: mobilization.mobilizationNumber || mobilization.driverName || `Mobilization #${mobilization.mobilizationId}`,
            fulfilledQuantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
            fulfillmentStatus: mobilization.status || 'CREATED',
            status: 'ACTIVE'
        }).subscribe({
            error: () => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: 'Mobilization was saved, but the requirement fulfillment link could not be updated.'
            })
        });
    }
}
